import { useEffect, useMemo, useState } from 'react';
import { useCanvasStore } from './store/canvas-store';
import { emptyState, framesById } from './store/commands';
import { FrameContent } from './render/FrameContent';
import { useTailwindCompile } from './style/useTailwindCompile';
import { loadPreviewDoc, subscribePreviewDoc } from './persistence/doc-source';
import { isPanelOwned, type CanvasDoc } from './store/types';

/**
 * Single-frame preview tab. Reached via the `#/frame/<id>` permalink. Loads the
 * doc from the (swappable) doc source, then renders just one frame with no
 * canvas chrome — real DOM, themed, effects + motion + scripts live, mirroring
 * the editor tab in real time.
 */
export function IsolatedFrameApp({ frameId }: { frameId: string }) {
  // Runtime Tailwind sheet — frame classes are unstyled without it.
  useTailwindCompile();

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const apply = (doc: CanvasDoc) => {
      useCanvasStore.getState().load({ ...emptyState(), doc });
      setLoaded(true);
    };
    const initial = loadPreviewDoc();
    if (initial) apply(initial);
    else setLoaded(true); // nothing to load → resolve to the not-found state
    return subscribePreviewDoc(apply);
  }, []);

  const frames = useCanvasStore((s) => s.doc.frames);
  const frame = frames.find((f) => f.id === frameId);
  const byId = useMemo(() => framesById(frames), [frames]);

  useEffect(() => {
    if (frame && 'title' in frame && frame.title) document.title = `${frame.title} — Vivarium`;
    else document.title = 'Frame — Vivarium';
  }, [frame]);

  if (!loaded) return null;

  // Tool frames that don't paint standalone (chat) or panel-owned frames have
  // nothing to show in isolation.
  const renderable = frame && !isPanelOwned(frame) && frame.kind !== 'chat';
  if (!renderable) {
    return (
      <div style={notFoundWrap}>
        <div style={notFoundCard}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Frame not found</div>
          <div style={{ opacity: 0.6, fontSize: 13 }}>
            No previewable frame with id <code>{frameId}</code> in this canvas.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={stage}>
      <div style={{ width: frame.w, height: frame.h ?? 'auto', position: 'relative' }}>
        {/* Isolated preview is DOM-only — no canvas paint loop, so onInvalidate
            is a no-op (FrameContent default invalidateCanvas no-ops off-canvas). */}
        <FrameContent frame={frame} byId={byId} />
      </div>
    </div>
  );
}

const stage: React.CSSProperties = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  padding: 24,
  boxSizing: 'border-box',
};
const notFoundWrap: React.CSSProperties = { ...stage };
const notFoundCard: React.CSSProperties = {
  textAlign: 'center',
  padding: '32px 40px',
  borderRadius: 14,
  border: '1px solid var(--color-border, #2a2a2a)',
  background: 'var(--color-surface, #161616)',
  color: 'var(--color-text, #eee)',
  maxWidth: 360,
};
